import { formatCurrencyCompact, formatDate } from '../../../../utils/format';
import { StatusBadge } from '../../../../components/base/StatusBadge';
import { getDebtReminderTimeline } from '../../../../utils/debtReminderScheduler';
import type { AdvanceRequest, Season } from '../../../../types';

interface LoanCardProps {
  loan: AdvanceRequest;
  season?: Season;
  onView: () => void;
  onDelete?: () => void;
}

const REMINDER_COLORS: Record<string, string> = {
  'T-7 ngày': 'bg-sky-100 text-sky-700',
  'T-3 ngày': 'bg-amber-100 text-amber-700',
  'T-1 ngày': 'bg-orange-100 text-orange-700',
  'T-12 giờ': 'bg-red-100 text-red-700',
  'T-6 giờ': 'bg-red-100 text-red-700',
  'T-1 giờ': 'bg-red-200 text-red-800',
  'Đến hạn': 'bg-red-100 text-red-700',
  'Quá hạn': 'bg-red-200 text-red-800',
};

export function LoanCard({ loan, season, onView, onDelete }: LoanCardProps) {
  // Only show reminder timeline for money loans
  const timeline = (loan.loai === 'Tiền') ? getDebtReminderTimeline(loan.id) : null;
  const triggeredReminders = timeline ? timeline.filter((r) => r.sent) : [];
  const hasReminders = triggeredReminders.length > 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded px-2 py-0.5">{loan.maKhoanVay || `#${loan.id.slice(-6).toUpperCase()}`}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              loan.loai === 'Tiền' ? 'bg-agrida-50 text-agrida-700' : 'bg-sky-50 text-sky-700'
            }`}>
              {loan.loai === 'Tiền' ? 'Tạm ứng tiền' : 'Tạm ứng vật tư'}
            </span>
            {loan.isOverLimit && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">
                <span className="w-3 h-3 inline-flex items-center justify-center mr-1"><i className="ri-error-warning-line" /></span>
                Vượt hạn mức
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            Ngày gửi: {formatDate(loan.ngayYeuCau)}{season && ` · ${season.name}`}
          </p>
        </div>
        <StatusBadge status={loan.status} />
      </div>

      <p className="text-sm font-semibold text-slate-900 break-words">{loan.mucDich}</p>

      {/* Reminder Timeline — compact chip row */}
      {hasReminders && (
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
          <span className="text-[10px] text-slate-400 shrink-0">
            <span className="w-3 h-3 inline-flex items-center justify-center"><i className="ri-notification-3-line" /></span>
          </span>
          {triggeredReminders.map((r) => (
            <span
              key={r.threshold}
              className={`shrink-0 text-[9px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${REMINDER_COLORS[r.label] || 'bg-slate-100 text-slate-600'}`}
            >
              {r.label}
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <p className="text-xs text-slate-400">Số tiền đề nghị</p>
          <p className="font-bold text-agrida-700">{formatCurrencyCompact(loan.soTien)}</p>
        </div>
        {loan.loai === 'Vật tư' && loan.tenVatTu && (
          <div>
            <p className="text-xs text-slate-400">Vật tư</p>
            <p className="text-slate-700 font-medium break-words">{loan.tenVatTu} {loan.soLuongVatTu && `(${loan.soLuongVatTu} ${loan.donViVatTu})`}</p>
          </div>
        )}
        {loan.thoiGianMongMuon && (
          <div>
            <p className="text-xs text-slate-400">Mong muốn nhận</p>
            <p className="text-slate-700">{formatDate(loan.thoiGianMongMuon)}</p>
          </div>
        )}
      </div>

      {loan.lyDo && (
        <div className="bg-red-50 rounded-xl p-2">
          <p className="text-xs text-red-600">{loan.lyDo}</p>
        </div>
      )}

      <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
        <button
          type="button"
          onClick={onView}
          className="text-xs font-medium text-slate-600 hover:text-slate-900 flex items-center gap-1"
        >
          <span className="w-3 h-3 flex items-center justify-center"><i className="ri-eye-line" /></span>
          Xem chi tiết
        </button>
        {onDelete && loan.status === 'Chờ kiểm tra' && (
          <button
            type="button"
            onClick={onDelete}
            className="text-xs font-medium text-red-600 hover:text-red-700 flex items-center gap-1"
          >
            <span className="w-3 h-3 flex items-center justify-center"><i className="ri-delete-bin-line" /></span>
            Xóa
          </button>
        )}
      </div>
    </div>
  );
}